import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';

interface LowHandAnimationProps {
  lowHandEvent: { playerIndex: number; timestamp: number } | null;
  opponentName?: string;
  myPlayerIndex?: number;
}

interface ParticleSeed {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  duration: number;
}

// Default seeds for low hand animation (reusing scopaParticleSeeds structure)
const defaultParticleSeeds: ParticleSeed[] = Array.from({ length: 40 }, () => ({
  x: Math.random(),
  y: Math.random(),
  scale: Math.random() * 0.5 + 0.5,
  rotate: Math.random() * 360,
  duration: Math.random() * 1.5 + 0.5,
}));

export default function LowHandAnimation({ lowHandEvent, opponentName, myPlayerIndex = 0 }: LowHandAnimationProps) {
  const particleSeeds = defaultParticleSeeds;
  const isMine = lowHandEvent ? lowHandEvent.playerIndex === myPlayerIndex : false;
  const displayName = isMine ? 'PER TE' : `PER ${opponentName || 'LA CPU'}`;

  return (
    <AnimatePresence>
      {lowHandEvent && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none overflow-hidden"
        >
          {/* animated background flash */}
          <motion.div
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={cn("absolute inset-0 backdrop-blur-sm", isMine ? 'bg-blue-500/30' : 'bg-purple-500/30')}
          />

          {/* Main text */}
          <motion.div
            initial={{ scale: 0.5, y: 50, rotate: -15, opacity: 0 }}
            animate={{ scale: 1, y: 0, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", bounce: 0.7, duration: 0.8 }}
            className="relative flex flex-col items-center z-10"
          >
            <motion.span
              className={cn(
                "text-6xl sm:text-8xl font-black italic tracking-tighter text-transparent bg-clip-text drop-shadow-[0_0_50px_rgba(59,130,246,0.8)]",
                isMine
                  ? "bg-gradient-to-br from-blue-300 via-blue-500 to-cyan-600"
                  : "bg-gradient-to-br from-purple-400 via-purple-600 to-violet-800 drop-shadow-[0_0_50px_rgba(147,51,234,0.8)]"
              )}
              style={{ WebkitTextStroke: '2px rgba(255,255,255,0.5)' }}
            >
              SBARAZZO!
            </motion.span>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={cn(
                "mt-4 px-6 py-2 bg-black/50 backdrop-blur-md rounded-full border tracking-widest text-lg sm:text-2xl font-black",
                isMine ? "border-blue-500/30 text-blue-100" : "border-purple-500/30 text-purple-100"
              )}
            >
              +3 PUNTI {displayName}
            </motion.div>
          </motion.div>

          {/* particles */}
          {Array.from({ length: 40 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 1,
                x: particleSeeds[i].x * (typeof window !== 'undefined' ? window.innerWidth : 800),
                y: particleSeeds[i].y * (typeof window !== 'undefined' ? window.innerHeight : 600),
                scale: particleSeeds[i].scale,
                rotate: 0
              }}
              animate={{
                opacity: 0,
                x: particleSeeds[i].x * (typeof window !== 'undefined' ? window.innerWidth : 800) + (particleSeeds[i].x * 400 - 200),
                y: particleSeeds[i].y * (typeof window !== 'undefined' ? window.innerHeight : 600) + (particleSeeds[i].y * 300 - 150),
                rotate: particleSeeds[i].rotate
              }}
              transition={{ duration: particleSeeds[i].duration, ease: "easeOut" }}
              className="absolute z-0 w-8 h-8 flex items-center justify-center text-3xl drop-shadow-lg"
            >
              {isMine ? ['🌊','💎','❄️','🔷','🌬️'][i % 5] : ['🎯','💫','✨','🎪','🔮'][i % 5]}
            </motion.div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}